import { type PropsWithChildren, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ManifestLink } from '@remix-pwa/sw';
import {
  Links,
  Meta,
  Scripts,
  UNSAFE_RemixContext,
  useLocation,
  useMatches,
  useRouteLoaderData,
  useSearchParams,
} from '@remix-run/react';
import cx from 'classnames';

import { AppGlobalsScript } from '~/components/AppGlobals';
import AdSensePixel from '~/components/Pixels/AdSensePixel';
import FacebookPixel from '~/components/Pixels/FacebookPixel';
import GA4Pixel from '~/components/Pixels/GA4Pixel';
import { GTMPixel, GTMScript } from '~/components/Pixels/GTMPixel';
import TiktokPixel from '~/components/Pixels/TiktokPixel';
import TwitterPixel from '~/components/Pixels/TwitterPixel';
import { useDisplayMode } from '~/hooks/useDisplayMode';
import useCookies from '~/hooks/useUtils/useCookies';
import type { Community } from '~/interfaces/community';
import type { RootLoaderData } from '~/root';
import {
  ASYNC_STYLESHEET_ATTRIBUTE,
  ASYNC_STYLESHEET_FAILED_ATTRIBUTE,
  CRITICAL_CSS_ATTRIBUTE,
} from '~/utils/criticalCss/constants';
import getRootStyles from '~/utils/getRootStyles';
import { I18N_RESOURCES_ELEMENT_ID } from '~/utils/i18nResources';

import { ComponentErrorBoundary } from '../ComponentErrorBoundary';
import { LAZY_BLOCKS_ELEMENT_ID } from '../DreamBuilder/lazyBlock';
import EmbedSupportScript from '../EmbedSupportScript';
import { FontStyles } from '../FontStyles';
import GlobalHeaderScript from '../Pixels/GlobalHeaderScript';
import GSCVerification from '../Pixels/GSCVerification';
import { ScrollRestoration } from '../ScrollRestoration';
import { resolvePwaHeadConfig, resolveThemeColor } from './pwaHead';
import { SHARED_STYLESHEET_HREFS } from './sharedStylesheets';

interface CommunityRouteLoaderData {
  community: Community | null;
}

type Props = PropsWithChildren<{
  title?: string;
}>;

const Document = ({ children }: Props) => {
  const { site, isProdEnv, noScript } = (useRouteLoaderData('root') ?? {}) as RootLoaderData;

  const location = useLocation();
  const { i18n } = useTranslation();

  const [searchParams] = useSearchParams();
  const preventScrollOnFilter =
    searchParams.has('content_tags') || (searchParams.has('q') && location.pathname === '/');

  const isPostPage = location.pathname.startsWith('/p/');

  let bodyClass = '';

  if (isPostPage) {
    bodyClass = 'bg-wt-background text-wt-text-on-background';
  }
  const gtmPixel = site?.viewable_site_version?.settings?.gtm_pixel_id;

  return (
    <html lang={i18n?.language || 'en'} className="h-full antialiased">
      <Head />
      <body suppressHydrationWarning className={cx(bodyClass)}>
        {isProdEnv && gtmPixel && (
          <ComponentErrorBoundary
            fallbackRender={() => {
              return null;
            }}
          >
            <GTMPixel pixelId={gtmPixel} />
          </ComponentErrorBoundary>
        )}
        {/** Prevent any user clicks when previewing so the user doesn't navigate to the wrong page in the iframe */}
        {children}
        {!preventScrollOnFilter && <ScrollRestoration />}
        {!noScript && <I18nResources />}
        {!noScript && <LazyBlocks />}
        {!noScript && <Scripts />}
      </body>
    </html>
  );
};

export default Document;

// Stable reference to prevent infinite re-renders in React 19
// Creating a new {} object on every render can cause update loops
const EMPTY_INITIAL_COOKIES = {};

const BEFORE_INSTALL_PROMPT_SCRIPT = `
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  window.__DeferredPrompt__ = e;
});
`;

// Runs before the links exist and listens on the document, so a sheet that loads or fails at once is still caught.
const ASYNC_STYLESHEET_SWAP_SCRIPT = `
document.addEventListener('load', function (event) {
  if (event.target.hasAttribute && event.target.hasAttribute('${ASYNC_STYLESHEET_ATTRIBUTE}')) event.target.media = 'all';
}, true);
document.addEventListener('error', function (event) {
  if (event.target.hasAttribute && event.target.hasAttribute('${ASYNC_STYLESHEET_ATTRIBUTE}')) event.target.setAttribute('${ASYNC_STYLESHEET_FAILED_ATTRIBUTE}', '');
}, true);
`;

type AsyncStylesheetProps = { href: string };

// Once the full sheet applies, the client drops the critical subset so a re-rendered <head> can't override its later rules.
// Until then it keeps the server's subset, or a failed or late sheet would leave the hydrated page unstyled.
const readCriticalCssUntilSheetApplies = (href: string) => {
  if (typeof document === 'undefined') return '';
  const link = document.querySelector<HTMLLinkElement>(`link[${ASYNC_STYLESHEET_ATTRIBUTE}][href="${href}"]`);
  if (link?.sheet && !link.hasAttribute(ASYNC_STYLESHEET_FAILED_ATTRIBUTE)) return '';
  return document.querySelector(`style[${CRITICAL_CSS_ATTRIBUTE}="${href}"]`)?.textContent ?? '';
};

const AsyncStylesheet = ({ href }: AsyncStylesheetProps) => (
  <>
    <link rel="preload" as="style" href={href} />
    <style
      {...{ [CRITICAL_CSS_ATTRIBUTE]: href }}
      dangerouslySetInnerHTML={{ __html: readCriticalCssUntilSheetApplies(href) }}
      suppressHydrationWarning
    />
    <link
      rel="stylesheet"
      href={href}
      media={typeof document === 'undefined' ? 'print' : 'all'}
      {...{ [ASYNC_STYLESHEET_ATTRIBUTE]: '' }}
      suppressHydrationWarning
    />
    <noscript>
      <link rel="stylesheet" href={href} />
    </noscript>
  </>
);

const RouteLinksWithAsyncStylesheets = () => {
  const remixContext = useContext(UNSAFE_RemixContext);
  const matches = useMatches();
  const manifest = remixContext?.manifest;

  const manifestWithoutRouteCss = useMemo(
    () =>
      manifest && {
        ...manifest,
        routes: Object.fromEntries(
          Object.entries(manifest.routes).map(([routeId, route]) => [routeId, { ...route, css: undefined }]),
        ),
      },
    [manifest],
  );

  if (!remixContext || !manifest || !manifestWithoutRouteCss) return <Links />;

  const routeStylesheetHrefs = [...new Set(matches.flatMap((match) => manifest.routes[match.id]?.css ?? []))];

  return (
    <>
      {routeStylesheetHrefs.map((href) => (
        <AsyncStylesheet key={href} href={href} />
      ))}
      <UNSAFE_RemixContext.Provider value={{ ...remixContext, manifest: manifestWithoutRouteCss }}>
        <Links />
      </UNSAFE_RemixContext.Provider>
    </>
  );
};

const PwaInstallPromptHead = ({
  showCommunityManifest,
  showPublicationManifest,
}: {
  showCommunityManifest: boolean;
  showPublicationManifest: boolean;
}) => (
  <ComponentErrorBoundary
    fallbackRender={() => {
      return null;
    }}
  >
    <script suppressHydrationWarning dangerouslySetInnerHTML={{ __html: BEFORE_INSTALL_PROMPT_SCRIPT }} />
    {showCommunityManifest && <link rel="manifest" href="/community/manifest.webmanifest" />}
    {showPublicationManifest && <ManifestLink />}
  </ComponentErrorBoundary>
);

export function Head() {
  const { site, isProdEnv, rootStyles } = (useRouteLoaderData('root') ?? {}) as RootLoaderData;
  const publication = site?.publication;
  const communityLayoutData = useRouteLoaderData('routes/community') as CommunityRouteLoaderData | undefined;
  const displayMode = useDisplayMode();
  const location = useLocation();
  const { cookies, cookiesSet } = useCookies({ initialCookies: EMPTY_INITIAL_COOKIES });

  const community = communityLayoutData?.community;
  const { isCommunityPwaEnabled, isPwaInstallPromptEnabled, showCommunityManifest, showPublicationManifest } =
    resolvePwaHeadConfig({
      pathname: location.pathname,
      communityPwaEnabled: community?.pwa_enabled,
      publicationPwaEnabled: publication?.web_template?.pwa_enabled,
    });

  // Compliance
  const cookiesBanner = site?.viewable_site_version?.settings?.cookie_banner_enabled;
  const cookiesLocalStorage = cookies?.beehiiv_honey;

  let marketingCookies = !cookiesBanner;
  let dataProcessorCookies = !cookiesBanner;

  if (cookiesBanner) {
    marketingCookies =
      cookiesBanner && cookiesLocalStorage
        ? JSON.parse(cookiesLocalStorage)?.marketing || false
        : !(cookiesBanner && !cookiesLocalStorage);
    dataProcessorCookies =
      cookiesBanner && cookiesLocalStorage
        ? JSON.parse(cookiesLocalStorage)?.dataProcessing || false
        : !(cookiesBanner && !cookiesLocalStorage);
  }

  // Pixels - Fall back to publication-level settings for legacy builder compatibility
  const facebookPixel = site?.viewable_site_version?.settings?.meta_pixel_id || site?.publication?.facebook_pixel;
  const twitterPixel = site?.viewable_site_version?.settings?.x_pixel_id || site?.publication?.twitter_pixel;
  const tiktokPixel = site?.viewable_site_version?.settings?.tiktok_pixel_id || site?.publication?.tiktok_pixel;
  const ga4Pixel = site?.viewable_site_version?.settings?.ga4_pixel_id || site?.publication?.google_analytics_4_pixel;
  const gtmPixel = site?.viewable_site_version?.settings?.gtm_pixel_id || site?.publication?.gtm_pixel;
  const gscCode =
    site?.viewable_site_version?.settings?.gsc_verification_code || site?.publication?.gsc_verification_code;
  const globalScript = site?.viewable_site_version?.settings?.global_header_script_blob;
  const adsensePublisherId = site?.publication?.has_adsense ? site?.adsense_publisher_id : undefined;

  const viewport = `width=device-width, initial-scale=1, shrink-to-fit=no${displayMode === 'standalone' ? ', user-scalable=no' : ''}`;
  const isPostPage = location.pathname.startsWith('/p/');
  const isProductionBuild = process.env.NODE_ENV === 'production';
  const themeColor = resolveThemeColor({
    isCommunityPwaEnabled,
    communityPrimaryColor: community?.primary_color,
    publicationBackgroundColor: publication?.web_theme?.background_color,
  });

  return (
    <head>
      <meta charSet="utf-8" />
      <meta name="viewport" content={viewport} />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="theme-color" content={themeColor} />
      {/* CRITICAL: AppGlobalsScript must succeed for app to function - don't wrap */}
      <AppGlobalsScript />
      {/* set default colors in case root loader fails and cant return root styles for error boundary*/}
      {/* these colors will be overridden below from getRootStyles() from root loader*/}
      <style
        dangerouslySetInnerHTML={{
          __html: `
:root {
  --wt-primary-color: #030712;
  --wt-text-on-primary-color: #F9FAFB;

  --wt-background-color: #FFFFFF;
  --wt-text-on-background-color: #030712;
}
`,
        }}
      />
      {isPwaInstallPromptEnabled && (
        <PwaInstallPromptHead
          showCommunityManifest={showCommunityManifest}
          showPublicationManifest={showPublicationManifest}
        />
      )}
      <Meta />
      <FontStyles />
      <style
        suppressHydrationWarning={true}
        dangerouslySetInnerHTML={{
          __html:
            isPostPage && publication?.web_theme
              ? getRootStyles(
                  publication,
                  site?.viewable_site_version?.settings?.post_page_background_color,
                  site?.viewable_site_version?.settings?.post_page_text_color,
                )
              : rootStyles,
        }}
      />
      {isProductionBuild && <script dangerouslySetInnerHTML={{ __html: ASYNC_STYLESHEET_SWAP_SCRIPT }} />}
      {isProductionBuild ? <RouteLinksWithAsyncStylesheets /> : <Links />}
      {/* Shared styles load after route CSS from <Links /> so they win equal-specificity conflicts. */}
      {SHARED_STYLESHEET_HREFS.map((href) =>
        isProductionBuild ? (
          <AsyncStylesheet key={href} href={href} />
        ) : (
          <link key={href} rel="stylesheet" href={href} />
        ),
      )}
      {isProdEnv && (
        <ComponentErrorBoundary
          fallbackRender={() => {
            return null;
          }}
        >
          {gscCode && <GSCVerification code={gscCode} />}
          {gtmPixel && <GTMScript dataProcessingCookies={dataProcessorCookies} pixelId={gtmPixel} />}
          {facebookPixel && <FacebookPixel marketingCookies={marketingCookies} pixelId={facebookPixel} />}
          {twitterPixel && <TwitterPixel marketingCookies={marketingCookies} pixelId={twitterPixel} />}
          {tiktokPixel && <TiktokPixel marketingCookies={marketingCookies} pixelId={tiktokPixel} />}
          {ga4Pixel && cookiesSet && (
            <GA4Pixel
              marketingCookies={marketingCookies}
              pixelId={ga4Pixel}
              consentModeEnabled={Boolean(publication?.ga4_consent_mode_enabled)}
            />
          )}
          {adsensePublisherId && <AdSensePixel marketingCookies={marketingCookies} pixelId={adsensePublisherId} />}
        </ComponentErrorBoundary>
      )}
      {globalScript && (
        <ComponentErrorBoundary
          fallbackRender={() => {
            return null;
          }}
        >
          <GlobalHeaderScript script={globalScript} />
        </ComponentErrorBoundary>
      )}
      <ComponentErrorBoundary
        fallbackRender={() => {
          return null;
        }}
      >
        <EmbedSupportScript />
      </ComponentErrorBoundary>
    </head>
  );
}

export function LazyBlocks() {
  const names = typeof window === 'undefined' ? '' : document.getElementById(LAZY_BLOCKS_ELEMENT_ID)?.textContent || '';
  return <script type="application/json" id={LAZY_BLOCKS_ELEMENT_ID} dangerouslySetInnerHTML={{ __html: names }} />;
}

export function I18nResources() {
  let resources = '';
  if (typeof window !== 'undefined') {
    resources = document.getElementById(I18N_RESOURCES_ELEMENT_ID)?.textContent || '';
  }
  return (
    <script type="application/json" id={I18N_RESOURCES_ELEMENT_ID} dangerouslySetInnerHTML={{ __html: resources }} />
  );
}
